import React from 'react';
import { motion } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiCheck, FiStar } = FiIcons;

const Pricing = () => {
  const plans = [
    {
      name: "Starter",
      price: 19,
      description: "Perfect for digital entrepreneurs starting their social media journey",
      features: [
        "5 social profiles",
        "150 scheduled posts per month",
        "Smart auto-posting",
        "Basic analytics & insights",
        "Content calendar view",
        "RSS feed integration (5 feeds)",
        "Standard support",
        "1 team member"
      ],
      popular: false,
      link: "https://dashboard.postifyr.com/rsab6205d20f11c9b3dc605d555af05899f"
    },
    {
      name: "Pro",
      price: 35,
      description: "Everything you need to dominate social media as a digital entrepreneur",
      features: [
        "15 social media profiles",
        "UNLIMITED scheduled posts",
        "AI-powered content suggestions",
        "Advanced analytics & performance",
        "Viral campaigns & contests",
        "Automatic post recycling",
        "Best time to post recommendations",
        "Priority support",
        "Team collaboration (2 users)"
      ],
      popular: true,
      badge: "MOST POPULAR",
      link: "https://dashboard.postifyr.com/rsa176125959b2e527cd97c7b79622c6fdb"
    },
    {
      name: "Growth",
      price: 69,
      description: "The all-in-one solution to manage your entire social media empire",
      features: [
        "25 social media profiles",
        "UNLIMITED posts & scheduling",
        "Advanced AI content creation",
        "Unlimited viral campaigns",
        "Video testimonial collection",
        "Premium analytics with custom reports",
        "Advanced team collaboration (5 users)",
        "White-label option",
        "VIP priority support"
      ],
      popular: false,
      link: "https://dashboard.postifyr.com/rsaa1e81bf231416c8be8aad9953477c954"
    }
  ];

  return (
    <section id="pricing" className="py-24 px-4 sm:px-6 lg:px-8 bg-brand-gray-50 scroll-mt-20">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <h2 className="text-4xl md:text-5xl font-bold text-brand-navy mb-6">
            Pricing That Actually Makes Sense
          </h2>
          <p className="text-xl md:text-2xl text-gray-600">
            No contracts. No per-user fees. No hidden costs. Just one simple price.
          </p>
        </motion.div>

        {/* Pricing Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-16 items-start">
          {plans.map((plan, index) => (
            <motion.div 
              key={index}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: index * 0.2 }}
              className={`relative bg-white rounded-2xl p-8 flex flex-col h-full ${plan.popular ? 'border-2 border-brand-teal shadow-2xl scale-105 z-10' : 'border border-gray-200 shadow-lg'}`}
            >
              {/* Popular Badge */}
              {plan.popular && (
                <div className="absolute top-0 right-0 transform translate-x-2 -translate-y-2">
                  <div className="bg-brand-yellow px-4 py-1 rounded-full flex items-center space-x-1 shadow-md">
                    <SafeIcon icon={FiStar} className="text-brand-navy text-xs fill-current" />
                    <span className="text-brand-navy font-bold text-xs tracking-wide">{plan.badge}</span>
                  </div>
                </div>
              )}

              {/* Plan Header */}
              <div className="mb-8">
                <h3 className="text-2xl font-bold text-brand-navy mb-4">{plan.name}</h3>
                <div className="mb-4 flex items-baseline">
                  <span className="text-5xl font-bold text-brand-navy">${plan.price}</span>
                  <span className="text-gray-500 ml-2">/month</span>
                </div>
                <p className="text-gray-600 text-sm leading-relaxed">{plan.description}</p>
              </div>

              {/* Features */}
              <div className="space-y-4 mb-8 flex-grow">
                {plan.features.map((feature, featureIndex) => (
                  <div key={featureIndex} className="flex items-start space-x-3">
                    <SafeIcon icon={FiCheck} className="text-brand-cyan text-lg mt-0.5 flex-shrink-0" />
                    <span className="text-gray-700 text-sm">{feature}</span>
                  </div>
                ))}
              </div>

              {/* CTA Button */}
              <a 
                href={plan.link}
                className={`w-full py-4 px-6 rounded-xl font-bold text-center transition-all duration-300 ${
                  plan.popular 
                    ? 'bg-brand-teal text-white hover:bg-brand-navy shadow-lg shadow-brand-teal/30 hover:-translate-y-1' 
                    : 'bg-brand-gray-50 text-brand-navy hover:bg-gray-100 border border-gray-200 hover:border-brand-teal'
                }`}
              >
                Start Free Trial →
              </a>
            </motion.div>
          ))}
        </div>

        {/* Below Pricing Info */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-12"
        >
          <p className="text-gray-500 text-lg mb-12">
            7-day free trial on all plans • No credit card required • Cancel anytime
          </p>

          {/* Money-Back Guarantee */}
          <div className="bg-white rounded-2xl p-8 shadow-sm border border-gray-200 max-w-lg mx-auto flex flex-col items-center text-center">
            <div className="w-16 h-16 bg-brand-teal/10 rounded-full flex items-center justify-center mb-4">
              <SafeIcon icon={FiCheck} className="text-3xl text-brand-teal" />
            </div>
            <h3 className="text-xl font-bold text-brand-navy mb-2">30-Day Money-Back Guarantee</h3>
            <p className="text-gray-600 text-sm">
              Try Postifyr risk-free. If you're not saving 10+ hours per week, we'll refund you. No questions asked.
            </p>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default Pricing;